// #region License

/**
 * @license
 * Copyright (C) JVS-Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

// #endregion

// #region Imports

import { Form, Ref } from '@mairistem/composants';

import { useControllable, useDebug, useEventCallback } from '@mairistem/tools';
import * as React from 'react';

import { useResizeObserver } from './utils/useResizeObserver';

import './Selector.less';

// #endregion

// #region Types

/**
 * Option for the Selector component.
 */
export interface SelectorOption {
  key: string;
  value: any;
  text: string;
  disabled?: boolean;
}

/**
 * Props for the Selector component.
 */
export interface SelectorProps {
  name?: string;
  value?: any;
  defaultValue?: any;
  options: SelectorOption[];
  onChange?: (e: React.FormEvent, data: { name: string; value: any }) => void;
}

/**
 * Represents the Selector component.
 */
export type SelectorComponent = React.FunctionComponent<SelectorProps>;

// #endregion

/**
 * Component for the Selector.
 *
 * @param props - The props for the component.
 * @returns The rendered Selector component.
 */
const Selector: SelectorComponent = (props) => {
  const { name = 'selector', value, defaultValue, options, onChange } = props;

  /** Debug. */
  useDebug('Selector', props);

  // #region Declarations

  /**
   * The value.
   */
  const [_value, setValue] = useControllable(value, defaultValue);

  /**
   * The active position of the selector.
   */
  const [activePosition, setActivePosition] = React.useState({
    width: 0,
    height: 0,
    translate: [0, 0],
  });

  /**
   * Refs for the labels in the selector.
   */
  const refs = React.useRef<Record<string, HTMLLabelElement>>({});

  /**
   * The resize observer reference and container rectangle.
   */
  const [observerRef, containerRect] = useResizeObserver();

  // #endregion

  // #region Fonctions

  /**
   * Handles the change event of the input element.
   *
   * @param e - The form event.
   * @param data - The data associated with the event.
   */
  const handleChange = useEventCallback((e: React.FormEvent<HTMLInputElement>, data: any) => {
    setValue(data.value);

    onChange?.(e, { name, value: data.value });
  });

  // #endregion

  // #region Effets

  /**
   * useEffect hook that calculates
   * and sets the active position of an element based on its type and container dimensions.
   *
   * @param _type - The type of the element.
   * @param refs - Refs object containing references to elements.
   * @param observerRef - Reference to the observer element.
   * @param containerRect - The dimensions of the container element.
   */
  React.useEffect(() => {
    if (_value in refs.current && observerRef.current) {
      const element = refs.current[_value];
      if (element) {
        const elementRect = element.getBoundingClientRect();
        const scaledValue = element.offsetWidth / elementRect.width;
        const width = element.clientWidth * scaledValue || 0;
        const height = element.clientHeight * scaledValue || 0;

        setActivePosition({
          width,
          height,
          translate: [
            element.parentElement!.offsetLeft - observerRef.current.offsetLeft,
            element.parentElement!.offsetTop - observerRef.current.offsetTop,
          ],
        });
      } else {
        setActivePosition({ width: 0, height: 0, translate: [0, 0] });
      }
    }
  }, [_value, containerRect]);

  // #endregion

  return (
    <Ref innerRef={observerRef}>
      <Form.Group className="selector" inline>
        <span
          className="indicator"
          style={{
            width: `${activePosition.width}px`,
            height: `${activePosition.height}px`,
            transform: `translate(${activePosition.translate[0]}px, ${activePosition.translate[1]}px)`,
          }}
        />
        {options.map((option) => (
          <Form.Field
            key={option.key}
            // @ts-expect-error
            style={{ paddingRight: '2em' }}
            className={option.disabled ? 'disabled' : undefined}
          >
            <input
              type="radio"
              name={`selector.${name}`}
              id={`selector-${name}-${option.key}`}
              value={option.text}
              checked={_value === option.value}
              disabled={option.disabled}
              onChange={(e) => !option.disabled && handleChange(e, option)}
            />
            <label
              htmlFor={`selector-${name}-${option.key}`}
              ref={(node) => {
                refs.current[option.value] = node!;
              }}
            >
              {option.text}
            </label>
          </Form.Field>
        ))}
      </Form.Group>
    </Ref>
  );
};

export default Selector;
